import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

export function StartSessionForm({ doctors, date, defaultDoctorId, onCreated }) {
  const initialDoctor = defaultDoctorId || (doctors.length === 1 ? doctors[0].id : '');
  const [doctorId, setDoctorId] = useState(initialDoctor);
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:00');
  const [fee, setFee] = useState('300');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!doctorId && defaultDoctorId) setDoctorId(defaultDoctorId);
  }, [defaultDoctorId, doctorId]);

  if (doctors.length === 0) {
    return (
      <p className="text-xs text-amber-700">
        No doctors found — add one in Admin → Users first.
      </p>
    );
  }

  const submit = async () => {
    if (!doctorId) return toast.error('Pick a doctor');
    setBusy(true);
    try {
      await ax.post('/api/clinic-ops/sessions', {
        doctor_id: doctorId,
        name: 'Walk-in clinic',
        date,
        start_time: start,
        end_time: end,
        slot_minutes: 15,
        consultation_fee: parseFloat(fee || 0),
      });
      toast.success('Session started — you can register patients now');
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start session');
    }
    setBusy(false);
  };

  return (
    <div className="bg-[#F8FAFC] border border-[#D5DEE8] rounded-xl p-3 space-y-2" data-testid="rec-start-session-form">
      {doctors.length > 1 ? (
        <Select value={doctorId} onValueChange={setDoctorId}>
          <SelectTrigger className="h-8 text-xs" data-testid="rec-start-doctor">
            <SelectValue placeholder="Doctor" />
          </SelectTrigger>
          <SelectContent>
            {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-xs text-[#5A6B7C]">Doctor: <span className="font-medium text-[#071B34]">{doctors[0]?.name}</span></p>
      )}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px]">Start</Label>
          <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 text-xs" data-testid="rec-start-time" />
        </div>
        <div>
          <Label className="text-[10px]">End</Label>
          <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 text-xs" data-testid="rec-end-time" />
        </div>
        <div>
          <Label className="text-[10px]">Fee ₹</Label>
          <Input type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)} className="h-8 text-xs" />
        </div>
      </div>
      <Button size="sm" onClick={submit} disabled={busy} className="w-full bg-[#F15A22] hover:bg-[#d94e1c] text-white"
        data-testid="rec-start-session-submit">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
        Start session
      </Button>
    </div>
  );
}
